import { Link } from "react-router-dom";
import { Eye, Compass, HeartHandshake, Globe2, BadgeCheck } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { IMAGES } from "@/lib/images";
import { Reveal, SectionHeading, Marquee } from "@/components/ui-bits";

export default function About() {
  const { t } = useLanguage();
  const cards = [
    [Eye, "about.vision.title", "about.vision.body"],
    [Compass, "about.mission.title", "about.mission.body"],
    [HeartHandshake, "about.values.title", "about.values.body"],
    [Globe2, "about.reach.title", "about.reach.body"],
    [BadgeCheck, "about.standards.title", "about.standards.body"],
  ];

  return (
    <div data-testid="about-page">
      <section className="hero-grain relative overflow-hidden bg-navy-950 py-24 lg:py-32">
        <img src={IMAGES.heroAlt} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/50 to-navy-950" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-gold">{t("about.eyebrow")}</p>
            <h1 className="max-w-3xl font-display text-4xl font-semibold text-white sm:text-5xl lg:text-6xl">{t("about.title")}</h1>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
          <Reveal>
            <div className="overflow-hidden rounded-lg shadow-2xl">
              <img src={IMAGES.boardroom} alt="Stratedge UK boardroom" className="h-[440px] w-full object-cover" loading="lazy" />
            </div>
          </Reveal>
          <div>
            <SectionHeading eyebrow={t("intro.eyebrow")} title={t("intro.title")} />
            <Reveal delay={0.1}>
              <p className="-mt-6 text-base leading-relaxed text-slate-600">{t("about.body1")}</p>
              <p className="mt-4 text-base leading-relaxed text-slate-600">{t("about.body2")}</p>
              <Link
                to="/consultation"
                data-testid="about-consultation-cta"
                className="mt-8 inline-block rounded-full bg-gold px-8 py-3.5 text-sm font-bold text-navy transition-colors hover:bg-gold-hover"
              >
                {t("nav.book")}
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map(([Icon, titleKey, bodyKey], i) => (
              <Reveal key={titleKey} delay={i * 0.06} className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}>
                <div data-testid={`about-card-${i}`} className="h-full rounded-lg border border-slate-200 bg-white p-8 shadow-sm transition-colors hover:border-gold/60">
                  <Icon className="h-7 w-7 text-gold-hover" />
                  <h3 className="mt-4 font-display text-2xl font-semibold text-navy">{t(titleKey)}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{t(bodyKey)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-20">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
          <div>
            <SectionHeading eyebrow={t("expertise.eyebrow")} title={t("expertise.title")} />
            <div className="-mt-4 flex flex-wrap gap-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <span key={i} className="rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700">
                  {t(`expertise.${i}`)}
                </span>
              ))}
            </div>
          </div>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 gap-4">
              <img src={IMAGES.keynote} alt="Keynote" className="h-52 w-full rounded-lg object-cover shadow-lg" loading="lazy" />
              <img src={IMAGES.strategy} alt="Strategy session" className="mt-8 h-52 w-full rounded-lg object-cover shadow-lg" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
